import { CommonModule, Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, finalize, take, tap } from 'rxjs';
import { userRoles } from '../../../environments/environment';
import { ButtonComponent } from '../../shared/components/custom-button/button.component';
import { TableComponent } from '../../shared/components/custom-table/table.component';
import { AddBulkDialogComponent } from '../../shared/components/dialog/add-bulk-upload/addBulk-dialog.component';
import { AddTeacherComponent } from '../../shared/components/dialog/add-teacher/add-teacher.component';
import {
  AlertDialogComponent,
  AlertDialogData,
} from '../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { ExportDropdownComponent } from '../../shared/components/export-dropdown/export-dropdown';
import { CustomToastComponent } from '../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../shared/services/http.service';
import { UrlParserService } from '../../shared/services/url-parser.service';

// Interface for dialog data
interface TeacherDialogData {
  initialData?: APIResponse;
  EditMode: boolean;
}

interface APIResponse {
  items: any[];
  totalRecords: any;
  totalRecord: any;
  totalRows: any;
  schoolId: string;
  schoolName: string;
  address: string;
  suburb: string;
  postcode: string;
  teacherId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  Active: boolean | string;
  dateOfBirth: string;
  assignedTeacher: string;
  name: string;
  id: string;
  status: string;
  createdAt: string;
  userId: string;
  schoolEmail?: string;
}

interface listAPIResponse {
  status: boolean;
  message: string;
  data: {
    totalRecord: number;
    totalRows: number;
    items: any[];
  };
}

interface UsersPagedResponse {
  items: APIResponse[];
  totalRecords?: number;
  totalRecord?: number;
  totalRows?: number;
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: APIResponse;
}

function isPagedResponse(
  data: UsersPagedResponse | APIResponse[],
): data is UsersPagedResponse {
  return !!(data as UsersPagedResponse).items;
}

@Component({
  selector: 'app-teachers',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    ButtonComponent,
    MatDialogModule,
    ReactiveFormsModule,
    ExportDropdownComponent,
  ],
  templateUrl: './teachers.component.html',
  styleUrls: ['./teachers.component.scss'],
})
export class TeachersComponent implements OnInit, AfterViewInit {
  @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
  @ViewChild('statusButton') showstatus!: TemplateRef<any>;
  @ViewChild('actionButtonsStudent') actionButtonsStudent!: TemplateRef<any>;
  @ViewChild(TableComponent) tableComponent!: TableComponent;
  @ViewChild('addTeacherContent') addTeacherContent!: TemplateRef<any>;
  @ViewChild('addSchoolContent') addSchoolContent!: TemplateRef<any>;

  constructor(
    private dialog: MatDialog,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private location: Location,
    private urlParser: UrlParserService, 
  ) {
    this.initForms();
  }

  form!: FormGroup;
  userForm!: FormGroup;
  editingTeacherIndex: number | null = null;
  editingSchool: any = null;
  viewMode = false;
  selectedTeacherID: string | null = null;
  selectedSchool: any | null = null;
  loading = false;
  dataLoading = false;
  columns: string[] = [
    'teacher name',
    'email',
    'school name',
    'phone/mobile',
    'created date',
    'status',
  ];
  exportColumns: string[] = [
    'Teacher Name',
    'Email',
    'School Name',
    'Phone/Mobile',
    'Status',
    'Created Date',
  ];
  studentcolumns: string[] = [
    'first name',
    'last name',
    'date of birth',
    'email',
  ];
  actionTemplateReady = false;
  schoolOptions: any[] = [];
  studentData: any[] = [];
  data: any[] = [];
  fullData: any[] = [];
  search: string = '';
  searchControl = new FormControl('');
  private currentRequestId = 0;
  private isPageChangeLoading = false;
  customErrorMessages = {
    firstName: { required: 'Please enter a first name.' },
    lastName: { required: 'Please enter a last name.' },
    email: {
      required: 'Please enter a valid email address.',
      email: 'Please enter a valid email',
    },
    phoneNumber: {
      required: 'Please enter a phone number.',
      pattern: 'Please enter a valid phone number.',
    },
    school: { required: 'Please select a school.' },
  };

  isProcessing = false;
  userRoles = {
    addNew: false,
    export: false,
    bulkUpload: false,
    listItem: {
      edit: false,
      view: false,
      delete: false,
      statusChange: false,
    },
  };
  userViewRoles = {
    edit: false,
    listItem: {
      view: false,
    },
  };

  currentPage: number = 1;
  pageSize: number = 10;
  totalRecords: number = 0;
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | '' = '';

  ngOnInit(): void {
     this.route.paramMap.subscribe((params) => {
    const encodedId = params.get('id');
    const id = encodedId ? this.urlParser.decodeId(encodedId) : null;
    this.viewMode = !!id;
    this.selectedTeacherID = id || null;

    if (this.viewMode && this.selectedTeacherID) {
      this.fetchViewTeachers(this.selectedTeacherID);
    }
  });

    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      const page = params['page'] ? parseInt(params['page']) : 1;
      this.currentPage = page;

      if (!this.viewMode) {
        this.fetchTeachers(this.currentPage);
      }
    });

    const user = localStorage.getItem('userRole');
    if (user && user in userRoles) {
      this.userRoles = userRoles[user as keyof typeof userRoles]?.teacher;
      this.userViewRoles =
        userRoles[user as keyof typeof userRoles]?.teacherView;
    }

    this.searchControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchValue) => {
        this.search = searchValue || '';
        this.currentPage = 1;
        this.updateUrlWithPage(1);
        this.fetchTeachers(this.currentPage);
      });
  }

  private initForms(): void {
    this.form = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ],
      ],
      phoneNumber: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/),
        ],
      ],
      school: ['', Validators.required],
    });

    this.userForm = this.fb.group({
      firstName: [''],
      lastName: [''],
      email: [''],
      school: [''],
      status: [''],
    });
  }

  updateSearch(searchValue: string): void {
    this.search = searchValue;
    this.searchControl.setValue(searchValue);
    this.cdr.detectChanges();
  }

  private getSortColumnName(column: string): string {
    const columnMapping: { [key: string]: string } = {
      'teacher name': 'TeacherName',
      'first name': 'FirstName',
      'last name': 'LastName',
      email: 'Email',
      'school name': 'SchoolName',
      'phone/mobile': 'MobileNumber',
      'phone number': 'MobileNumber',
      school: 'SchoolName',
      status: 'Status',
      'created date': 'CreatedAt',
      'date registered': 'CreatedAt',
      teachername: 'TeacherName',
      firstname: 'FirstName',
      lastname: 'LastName',
      schoolname: 'SchoolName',
      phonenumber: 'MobileNumber',
      createddate: 'CreatedAt',
    };

    return columnMapping[column.toLowerCase()] || column;
  }

  fetchTeachers(page: number = 1): void {
    this.dataLoading = true;
    const requestId = ++this.currentRequestId;

    let url: string;
    if (this.search && this.search.trim() !== '') {
      url = `teachers/get/search?search=${encodeURIComponent(this.search)}&page=${page}`;
      if (this.sortColumn && this.sortDirection) {
        const sortColumnName = this.getSortColumnName(this.sortColumn);
        url += `&orderby=${this.sortDirection}&orderbyname=${sortColumnName}`;
      }
    } else {
      url = `teachers?page=${page}`;
      if (this.sortColumn && this.sortDirection) {
        const sortColumnName = this.getSortColumnName(this.sortColumn);
        url += `&orderby=${this.sortDirection}&orderbyname=${sortColumnName}`;
      }
    }

    this.httpService.get<listAPIResponse>(url).subscribe({
      next: (response) => {
        if (requestId !== this.currentRequestId) return;

        const items = response?.data?.items || [];
        this.totalRecords =
          response?.data?.totalRecord || response?.data?.totalRows;

        this.fullData = items.map((teacher) => {
          const createdAt = teacher?.createdAt
            ? new Date(teacher.createdAt).toLocaleDateString('en-GB', { timeZone: 'Australia/Brisbane' })
            : '-';
          const teacherName =
            teacher.firstName && teacher.lastName
              ? `${teacher.firstName} ${teacher.lastName}`
              : teacher.firstName || teacher.lastName || '-';

          return {
            id: teacher.teacherId || '-',
            name: teacherName,
            firstName: teacher.firstName || '-',
            lastName: teacher.lastName || '-',
            'teacher name': teacherName,
            'first name': teacher.firstName || '-',
            'last name': teacher.lastName || '-',
            email: teacher.email || '-',
            'school name': teacher.schoolName || '-',
            'phone/mobile': teacher.mobileNumber || '-',
            'phone number': teacher.mobileNumber || '-',
            school: teacher.schoolName || '-',
            status: teacher.status ?? 'Active',
            'created date': createdAt,
            'Date registered': createdAt,
            userId: teacher?.userId ?? '-',
            schoolId: teacher?.schoolId,
            isStatusClickable: true,
          };
        });

        this.data = [...this.fullData];
        this.dataLoading = false;
        this.isPageChangeLoading = false;
        this.currentPage = page;
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (requestId !== this.currentRequestId) return;

        console.error('Failed to load teachers:', err);
        this.fullData = [];
        this.data = [];
        this.dataLoading = false;
        this.isPageChangeLoading = false;
        this.showErrorToast(err?.message);
      },
    });
  }

  onSort(event: {
    direction: 'asc' | 'desc' | '';
    column: string | null;
  }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    this.currentPage = 1;
    this.updateUrlWithPage(this.currentPage);
    this.fetchTeachers(this.currentPage);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || this.isPageChangeLoading) return;

    this.isPageChangeLoading = true;
    this.currentPage = page;
    this.updateUrlWithPage(page);
    this.fetchTeachers(page);
  }

  private updateUrlWithPage(page: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: page,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  applyFilter(): void {
    this.currentPage = 1;
    this.updateUrlWithPage(1);
    this.fetchTeachers(1);
  }

  hasFilterInput(): boolean {
    const { firstName, lastName, email, school, status } = this.userForm.value;
    return !!(
      firstName?.trim() ||
      lastName?.trim() ||
      email?.trim() ||
      school?.trim() ||
      status
    );
  }

  transformApiData(items: any[]): any[] {
    return items.map((user) => ({
      'Teacher Name':
        [user.firstName, user.lastName].filter(Boolean).join(' ') || '-',
      Email: user.email ?? '-',
      'School Name': user?.schoolName ?? '-',
      'Phone/Mobile': user.mobileNumber ?? '-',
      Status: user.status ?? 'Active',
      'Created Date': user.createdAt
        ? new Date(user.createdAt).toLocaleDateString('en-GB', { timeZone: 'Australia/Brisbane' })
        : '-',
    }));
  }

  fetchSchoolsDropDown() {
    this.loading = true;
    return this.httpService.get<ApiDataResponse>(`Schools/dropdown`).pipe(
      tap({
        next: (response) => {
          if (Array.isArray(response.data)) {
            this.schoolOptions = response.data.map((item) => ({
              label: item.name,
              value: item.id,
            }));
          } else {
            this.schoolOptions = [];
          }
        },
        error: (err) => {
          console.error('Failed to load schools:', err);
          this.schoolOptions = [];
        },
      }),
      finalize(() => {
        this.loading = false;
      }),
    );
  }

  fetchViewTeachers(id: string): void {
    this.loading = true;
    this.httpService.get<ApiDataResponse>(`teachers/${id}`).subscribe({
      next: (response) => {
        if (response.data && !Array.isArray(response.data)) {
          this.selectedSchool = response.data;
          this.fetchViewTable(id);
        } else {
          this.selectedSchool = null;
          this.showErrorToast('Teacher data not found');
        }
      },
      error: (err) => {
        console.error('Failed to load teacher details:', err);
        this.selectedSchool = null;
        this.showErrorToast(err?.message);
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  fetchViewTable(id: string): void {
    this.loading = true;
    let schoolId = this.selectedSchool?.schoolId;
    this.httpService
      .get<ApiDataResponse>(`schools/${schoolId}/students`)
      .subscribe({
        next: (response) => {
          if (Array.isArray(response?.data)) {
            this.studentData = response.data.map((student) => ({
              id: student.studentId || '-',
              'first name': student.firstName || '-',
              'last name': student.lastName || '-',
              'date of birth': student.dateOfBirth
                ? new Date(student.dateOfBirth).toLocaleDateString('en-GB', {
                    timeZone: 'Australia/Brisbane',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                : '-',
              email: student.schoolEmail || '-',
            }));
          } else {
            this.studentData = [];
          }
        },
        error: (err) => {
          console.error('Failed to load students:', err);
          this.studentData = [];
          this.showErrorToast(err?.message);
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  getExportApiUrl(): string {
    if (this.search && this.search.trim() !== '') {
      return 'teachers/get/search/exports';
    } else {
      return 'teachers/exports';
    }
  }

  getCurrentExportParams(): any {
    const params: any = {
      orderby: this.sortDirection || 'desc',
      orderbyname: this.sortColumn
        ? this.getSortColumnName(this.sortColumn)
        : 'CreatedAt',
    };

    if (this.search && this.search.trim() !== '') {
      params.search = this.search;
    }

    return params;
  }

  onExportComplete(type: 'csv' | 'pdf' | 'excel'): void {
    this.showSuccessToast(
      `Export completed successfully as ${type.toUpperCase()}`,
    );
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      control.updateValueAndValidity();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  openAddBulkDialog(width: string = '66.375rem') {
    const dialogRef = this.dialog.open(AddBulkDialogComponent, {
      data: {
        title: 'Bulk upload',
        confirmText: 'Upload',
        cancelText: 'Cancel',
        template: this.addTeacherContent,
        type: 'teacher',
      },
      width,
      maxWidth: '100vw',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fetchTeachers(this.currentPage);
      }
    });
  }

  openAddTeacherDialog(
    teacherToEdit: APIResponse | undefined = undefined,
    isEdit: boolean = false,
    width: string = '50.5rem',
  ) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.editingTeacherIndex = null;
    this.form.reset();

    const dialogConfig: MatDialogConfig<TeacherDialogData> = {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: teacherToEdit, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    };

    const dialogRef = this.dialog.open(AddTeacherComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (this.viewMode && this.selectedTeacherID) {
          this.fetchViewTeachers(this.selectedTeacherID);
        } else {
          this.fetchTeachers(this.currentPage);
        }
      }
      this.isProcessing = false;
    });
  }

 onView(teacher: any): void {
  const encodedId = this.urlParser.encodeId(teacher.id.toString());
  this.router.navigate(['/teachers/view', encodedId]);
}

  goBack() {
    this.location.back();
  }

  onEdit(teacher: any): void {
    this.openAddTeacherDialog(teacher, true);
  }

  onStatusUpdate(event: { row: any; id: any }): void {
    if (!this.userRoles.listItem.statusChange) return;
    const { row } = event;
    const currentStatus = row.status;
    const newStatus = currentStatus === 'Active' ? false : true;

    const dialogData: AlertDialogData = {
      title: 'Are you sure!',
      message: `Are you sure you want to ${currentStatus === 'Active' ? 'Deactivate' : 'Activate'} ?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    this.dialog
      .open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.httpService
            .put<ApiDataResponse>(`users/${row?.userId}/toggle-status`, {
              status: newStatus,
            })
            .subscribe({
              next: (response) => {
                if (response.status) {
                  const newStatusValue = newStatus ? 'Active' : 'Inactive';
                  const fullDataIndex = this.fullData.findIndex(
                    (s) => s.id === row.id,
                  );
                  if (fullDataIndex !== -1) {
                    this.fullData[fullDataIndex] = {
                      ...this.fullData[fullDataIndex],
                      status: newStatusValue,
                    };
                  }
                  const dataIndex = this.data.findIndex((s) => s.id === row.id);
                  if (dataIndex !== -1) {
                    this.data[dataIndex] = {
                      ...this.data[dataIndex],
                      status: newStatusValue,
                    };
                  }

                  this.showSuccessToast(response.message);
                } else {
                  this.showErrorToast(response.message);
                }
              },
              error: (err) => this.showErrorToast(err?.message),
            });
        }
      });
  }

  onDelete(teacher: any): void {
    const fullName =
      `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim() ||
      'Teacher';
    const dialogData: AlertDialogData = {
      title: 'Confirm delete',
      message: `Are you sure want to delete ?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.httpService
          .delete<ApiDataResponse>(`teachers/${teacher.id}`)
          .subscribe({
            next: (response) => {
              if (response.status) {
                this.fullData = this.fullData.filter(
                  (u) => u.id !== teacher.id,
                );
                this.data = this.data.filter((u) => u.id !== teacher.id);
                this.adjustPaginationAfterDelete();
                this.showSuccessToast(response.message);
              } else {
                this.showErrorToast(response.message);
              }
            },
            error: (err) => {
              console.error('Failed to delete teacher:', err);
              this.showErrorToast(err?.message);
            },
          });
      }
    });
  }

  private adjustPaginationAfterDelete(): void {
    const totalPages = Math.ceil(this.totalRecords / this.pageSize);
    if (this.currentPage > totalPages && totalPages > 0) {
      this.currentPage = totalPages;
      this.updateUrlWithPage(this.currentPage);
      this.fetchTeachers(this.currentPage);
    } else {
      this.fetchTeachers(this.currentPage);
    }
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }

  getTeacherFullName(): string {
    if (!this.selectedSchool) return 'Teacher';
    return (
      `${this.selectedSchool.firstName || ''} ${this.selectedSchool.lastName || ''}`.trim() ||
      'Teacher'
    );
  }

  getTeacherInitials(): string {
    const first = this.selectedSchool?.firstName?.[0] ?? '';
    const last = this.selectedSchool?.lastName?.[0] ?? '';
    return (first + last).toUpperCase();
  }

  getTeacherStatusLabel(): string {
    const isActive: boolean | null | undefined = this.selectedSchool?.isActive;
    const hasLoggedIn: boolean = this.selectedSchool?.hasLoggedIn ?? false;

    if (isActive === false) return 'Inactive';
    if (isActive === true && hasLoggedIn) return 'Registered';
    if (isActive === true && !hasLoggedIn) return 'Pre registered';

    // isActive is null — no account created yet
    return 'Pre registered';
  }

  getTeacherStatusColor(): string {
    const label = this.getTeacherStatusLabel();
    const colorMap: Record<string, string> = {
      'Registered': '#4caf50',
      'Pre registered': '#f5a623',
      'Inactive': '#f5a623',
    };
    return colorMap[label] ?? '#9e9e9e';
  }

  onStudentView(student: any): void {
    this.router.navigate(['/students/view', student.id]);
  }

  ngAfterViewInit() {
    this.actionTemplateReady = true;
    this.cdr.detectChanges();
  }
}
